// 📄 components/credenciales/FlujoAprobacionCredencial.tsx
// "Flujo de aprobación para obtenerla" (docs/plans/plan-revelado-credenciales.md, R2/R3):
// quién guardó la credencial, quién aprueba si otro la pide, si ya fue
// vista, y los botones para verla o pedirla (ObtenerCredencial.tsx). Quien la guardó o el owner/admin de la organización eligen los
// aprobadores; el resto la ve en solo lectura. Todo pasa por onehubApi
// (credencialAccesoService.ts), la política no se lee directo de Firestore.
import React, { useCallback, useEffect, useState } from 'react';
import { ShieldCheck, RefreshCw, Save, AlertTriangle, X, Eye, History } from 'lucide-react';
import {
  AccesoCredencial,
  CredencialRef,
  ModoAprobacion,
  obtenerAcceso,
  guardarPolitica
} from '../../services/credencialAccesoService.ts';
import ObtenerCredencial from './ObtenerCredencial.tsx';
import { HistorialCredencialModal } from './HistorialCredencial.tsx';

interface FlujoAprobacionCredencialProps {
  credencial: CredencialRef;
  // Cambiarlo fuerza a recargar (p. ej. después de guardar una contraseña nueva).
  version?: number;
}

const nombreDe = (p: { nombre: string; email: string; uid: string }) => p.nombre || p.email || p.uid;
const fechaCorta = (iso: string | null) => (iso ? new Date(iso).toLocaleString() : '');

export default function FlujoAprobacionCredencial({ credencial, version = 0 }: FlujoAprobacionCredencialProps) {
  const [acceso, setAcceso] = useState<AccesoCredencial | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [aprobadores, setAprobadores] = useState<string[]>([]);
  const [modo, setModo] = useState<ModoAprobacion>('uno');
  const [guardando, setGuardando] = useState(false);
  const [mensaje, setMensaje] = useState<string | null>(null);
  const [verHistorial, setVerHistorial] = useState(false);

  const cargar = useCallback(() => {
    setLoading(true);
    setError(null);
    return obtenerAcceso(credencial)
      .then((a) => {
        setAcceso(a);
        setAprobadores(a.politica.aprobadores.map((x) => x.uid));
        setModo(a.politica.modo);
      })
      .catch((err) => {
        // 404: todavía no hay contraseña guardada (o no participas): no hay nada que mostrar.
        if (err?.status === 404) setAcceso(null);
        else setError(err.message || 'No se pudo cargar el flujo de aprobación.');
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [credencial.tipo, credencial.clienteId, credencial.apiId, credencial.projectId, credencial.credId]);

  useEffect(() => { cargar(); }, [cargar, version]);

  const toggle = (uid: string) => {
    setMensaje(null);
    setAprobadores((prev) => (prev.includes(uid) ? prev.filter((x) => x !== uid) : [...prev, uid]));
  };

  const handleGuardar = async () => {
    setGuardando(true);
    setError(null);
    setMensaje(null);
    try {
      const r = await guardarPolitica(credencial, aprobadores, aprobadores.length > 1 ? modo : 'uno');
      setMensaje(r.solicitudesCanceladas
        ? `Aprobadores guardados. Se cancelaron ${r.solicitudesCanceladas} solicitud(es) pendiente(s): se pidieron con la regla anterior.`
        : 'Aprobadores guardados.');
      await cargar();
    } catch (err: any) {
      setError(err.message || 'No se pudieron guardar los aprobadores.');
    } finally {
      setGuardando(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-xs text-gray-500 py-2">
        <RefreshCw className="w-3.5 h-3.5 animate-spin" /> Cargando flujo de aprobación...
      </div>
    );
  }
  if (!acceso && !error) return null;

  const pol = acceso?.politica;
  const guardados = pol?.aprobadores.map((x) => x.uid) || [];
  const huboCambios = acceso && (
    aprobadores.length !== guardados.length || aprobadores.some((u) => !guardados.includes(u)) ||
    (aprobadores.length > 1 && modo !== pol?.modo) || (pol?.porDefecto && aprobadores.length === 0)
  );

  const resumen = () => {
    if (!pol) return null;
    if (pol.aprobadores.length) {
      const nombres = pol.aprobadores.map((a) => `${nombreDe(a)}${a.disponible ? '' : ' (ya no está en el proyecto)'}`).join(', ');
      return pol.aprobadores.length > 1
        ? `Aprueban: ${nombres} — ${pol.modo === 'todos' ? 'tienen que aprobar todos' : 'basta con uno'}.`
        : `Aprueba: ${nombres}.`;
    }
    if (pol.creador?.activo) return `Sin aprobadores elegidos: la aprueba quien la guardó (${nombreDe(pol.creador)}).`;
    return 'Sin aprobadores elegidos: la aprueba el owner o un admin de la organización.';
  };

  return (
    <div className="bg-black/20 border border-white/10 rounded-xl p-4 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h4 className="text-xs font-semibold text-gray-300 flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-blue-400" /> Flujo de aprobación para obtenerla
        </h4>
        <div className="flex items-center gap-1">
          {acceso?.yo.puedeVerHistorial && (
            <button type="button" onClick={() => setVerHistorial(true)} className="flex items-center gap-1 px-2 py-1 rounded text-xs text-gray-400 hover:text-white hover:bg-white/10">
              <History className="w-3.5 h-3.5" /> Historial
            </button>
          )}
          <button type="button" onClick={cargar} title="Recargar" className="p-1 rounded text-gray-500 hover:text-white hover:bg-white/10">
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-2.5 rounded-lg text-xs">{error}</div>}
      {mensaje && <div className="bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 p-2.5 rounded-lg text-xs">{mensaje}</div>}

      {pol && (
        <div className="space-y-1.5 text-xs text-gray-400">
          <p>
            {pol.creador
              ? <>Guardada por <span className="text-gray-200">{nombreDe(pol.creador)}</span>{pol.creador.activo ? ' — puede verla sin pedir aprobación.' : ' (ya no está en el proyecto).'}</>
              : 'Se guardó antes de esta función: no se sabe quién la guardó.'}
          </p>
          <p>{resumen()}</p>
          {pol.expuesta && (
            <p className="flex items-start gap-1.5 text-amber-300/90 bg-amber-500/10 border border-amber-500/20 rounded-lg p-2">
              <Eye className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              <span>Expuesta: la vio {nombreDe(pol.expuesta)} el {fechaCorta(pol.expuesta.en)}. Considera cambiarla; al guardar una nueva deja de estar expuesta.</span>
            </p>
          )}
        </div>
      )}

      {acceso && <ObtenerCredencial acceso={acceso} onCambio={cargar} />}

      {acceso?.yo.puedeGestionarPolitica && (
        <div className="space-y-3 pt-2 border-t border-white/10">
          <p className="text-xs text-gray-500">
            Elige quién aprueba cuando otra persona la pida. Si no eliges a nadie, la aprueba quien la guardó
            {pol?.creador ? '' : ' (o, si no se sabe, el owner/admin de la organización)'}. Nadie aprueba su propia solicitud, y todo queda en el historial.
          </p>
          <div className="max-h-52 overflow-y-auto space-y-1 pr-1">
            {acceso.elegibles.map((e) => (
              <label key={e.uid} className="flex items-center gap-2.5 text-sm text-gray-300 cursor-pointer hover:text-white">
                <input type="checkbox" checked={aprobadores.includes(e.uid)} onChange={() => toggle(e.uid)} className="w-4 h-4 accent-blue-500" />
                <span className="truncate">{nombreDe(e)}</span>
                {e.email && e.nombre && <span className="text-[11px] text-gray-500 truncate">{e.email}</span>}
                {e.esCliente && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 flex-shrink-0">Cliente</span>}
                {e.esGestorOrg && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-indigo-500/15 text-indigo-300 flex-shrink-0">Owner/Admin</span>}
              </label>
            ))}
          </div>

          {aprobadores.length > 1 && (
            <div className="flex flex-wrap gap-4 text-xs text-gray-300">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name={`modo-${acceso.etiqueta}`} checked={modo === 'uno'} onChange={() => setModo('uno')} className="accent-blue-500" />
                Basta con que apruebe uno
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name={`modo-${acceso.etiqueta}`} checked={modo === 'todos'} onChange={() => setModo('todos')} className="accent-blue-500" />
                Tienen que aprobar todos
              </label>
            </div>
          )}
          {modo === 'todos' && aprobadores.length > 1 && (
            <p className="text-[11px] text-gray-500 flex items-start gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" />
              Si alguno deja el proyecto, nadie podrá pedirla hasta que se reasignen los aprobadores.
            </p>
          )}

          <div className="flex justify-end">
            <button
              type="button"
              onClick={handleGuardar}
              disabled={guardando || !huboCambios}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-40 px-3.5 py-1.5 rounded-lg text-xs font-semibold text-[#fff] transition-all"
            >
              {guardando ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              Guardar aprobadores
            </button>
          </div>
        </div>
      )}
      {verHistorial && acceso && (
        <HistorialCredencialModal credencial={acceso.credencial} titulo={acceso.etiqueta} onClose={() => setVerHistorial(false)} />
      )}
    </div>
  );
}

/** El mismo panel en una ventana, para tarjetas (APIs, Vault). */
export function FlujoAprobacionModal({ credencial, titulo, onClose }: { credencial: CredencialRef; titulo: string; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[rgb(var(--c-surface))] border border-white/10 rounded-2xl w-full max-w-lg shadow-2xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-white/10 flex-shrink-0">
          <h3 className="text-base font-medium text-white truncate">{titulo}</h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-white flex-shrink-0"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 overflow-y-auto">
          <FlujoAprobacionCredencial credencial={credencial} />
        </div>
      </div>
    </div>
  );
}
